import cx from 'classnames';
import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Breakpoint } from 'antd/lib/_util/responsiveObserve';
import { useEffect, useRef, MutableRefObject, Key } from 'react';
import { Space, Button, Row, Dropdown, Menu, InputRef } from 'antd';
import { FilterConfirmProps, FilterDropdownProps } from 'antd/lib/table/interface';

import '../Clients/Clients.scss';
import { RootState } from '../../state/store';
import Icon from '../../common/components/Icon/Icon';
import { getCurrentTheme } from '../../helpers/theme';
import { changeHeader } from '../AppHeader/headerSlice';
import { LrvTag } from '../../common/components/LrvTag/LrvTag';
import { GenericParam } from '../../common/interfaces/commons';
import { LrvInput } from '../../common/components/LrvInput/LrvInput';
import { LrvTable } from '../../common/components/LrvTable/LrvTable';
import { ExtraActionsButton } from '../../common/components/buttons/ExtraActionsButton';
import { setOnboardingType, setShouldRun, setRun, goToOnboardingNextStep, setStepIndex, setExistsUserData } from '../../common/components/Onboarding/OnboardingSlice';
import { isPromoterRole, isSalesRole, isSuperAdmin, LARVIA_ID, onboardingTypes, isBusinessManagerRole, isSalesManagerRole, userOptions, THEME } from '../../config/commons';

import styles from './Users.module.scss';
import * as usersSlice from './usersSlice';
import { NewUserForm } from './NewUserForm';
import { EditUserForm } from './EditUserForm';
import { UserSubHeader } from './UserSubHeader';
import { User, UserMapData } from './interfaces';
import AssigmentBalances from './AssigmentBalances';
import { getValueActiveUsers, USERS } from './helpers';
import { ResetPasswordForm } from './ResetPasswordForm';

function Users () {
  const dispatch = useDispatch();
  const [t] = useTranslation();

  const {
    users,
    isUsersLoading,
    selectedUser,
    filters,
  } = useSelector((state: RootState) => state.users);
  
  const { company } = useSelector((state: RootState) => state.header);
  const { userStatus } = filters;

  const refSearchName = useRef<InputRef | null>(null);
  const refSearchEmail = useRef<InputRef | null>(null);

  const {
    existsUserData,
    run: isRunningOnboarding,
    user: onboardingUserData,
  } = useSelector((state: RootState) => state.onboarding);

  const hasPermissionToManageCompanies = isSuperAdmin() || isSalesRole() || isSalesManagerRole() || isPromoterRole() || isBusinessManagerRole();

  const theme = getCurrentTheme();
  const isLightTheme = theme === THEME.LIGHT;

  useEffect(() => {
    return () => {
      dispatch(setExistsUserData(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (existsUserData && !onboardingUserData.onboarding?.usersSection) {
      setTimeout(() => {
        dispatch(setStepIndex(0));
        dispatch(setOnboardingType(onboardingTypes.USERS_SECTION));
        dispatch(setShouldRun(true));
        dispatch(setRun(true));
      }, 500);
    }
  }, [dispatch, onboardingUserData, existsUserData]);

  useEffect(() => {
    dispatch(changeHeader({ title: 'users.title' }));
  }, [dispatch]);

  useEffect(() => {
    if (!company._id) {
      return;
    }

    if (hasPermissionToManageCompanies) {
      const active = getValueActiveUsers(USERS.ACTIVE);
      dispatch(usersSlice.fetchUsers({ company, active }));
      dispatch(usersSlice.setSelectedUser({ _id: '', companyId: '', firstName: '', lastName: '', email: '', active: true, roles: [], allowCompanyBalanceConsumption: false }));
      return;
    }

    dispatch(usersSlice.fetchUsers({ company, active: true }));
    dispatch(usersSlice.fetchCampus());
  }, [dispatch, company._id, hasPermissionToManageCompanies]);

  const usersData: UserMapData[] = users ? users.map((user: User, index) => {
    return {
      key: index,
      _id: user._id,
      name: `${user.firstName} ${user.lastName}`,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      active: user.active,
    };
  }) : [];

  const columns: ColumnsType<UserMapData> = [
    {
      key: 1,
      title: t('users.user'),
      dataIndex: 'name',
      ellipsis: { showTitle: false },
      width: '30%',
      ...getColumnSearchProps('name', refSearchName),
    },
    {
      key: 2,
      title: t('users.email'),
      dataIndex: 'email',
      ellipsis: { showTitle: false },
      width: '30%',
      responsive: ['sm'] as Breakpoint[],
      ...getColumnSearchProps('email', refSearchEmail),
    },
    {
      key: 3,
      dataIndex: 'active',
      width: '30%',
      render: (_, record: UserMapData) => {
        return (
          <Space align='center' className={styles.statusCell} size={5}>
            {renderStatusTag(record)}
          </Space>
        );
      },
    },
    {
      key: 4,
      width: '10%',
      render: (_, record: UserMapData) => {
        return (
          <div className={styles.optionsCell}>
            {renderDropdownUserOptions(record)}
          </div>
        );
      },
    }
  ];

  const renderStatusTag = (record: UserMapData) => {
    if (record.active) {
      // if return null, table will display an "-" in the empty cell
      return <div></div>;
    }

    const id = `tag_${record._id}`;
    return (
      <LrvTag id={id} type='info' className={styles.statusTag}>{t('users.userInactive')}</LrvTag>
    );
  };

  const renderDropdownUserOptions = (user: UserMapData) => {
    return (
      <Dropdown
        overlay={() => menuItemsUserOptions(user)}
        trigger={['click']}
        placement='bottomRight'
      >
        <ExtraActionsButton
          onClick={(e) => e.stopPropagation()}
          id={`button_${user._id}`}
        />
      </Dropdown>
    );
  };

  const menuItemsUserOptions = (user: UserMapData) => {
    return (
      <Menu
        onClick={({ key, domEvent }) => {
          domEvent.stopPropagation();

          switch (key) {
            case userOptions.EDIT:
              onEditUserOption(user);
              break;

            case userOptions.CHANGE_PASSWORD:
              onChangePasswordOption(user);
              break;
          }
        }}
      >
        <Menu.Item id='menu_option_edit' key={userOptions.EDIT} className={styles.menuItemOptions}>
          <Icon name='edit' theme={theme} className={styles.icon} />
          <span>{t('users.editUser')}</span>
        </Menu.Item>

        <Menu.Item disabled={isRunningOnboarding} id='menu_option_reset_password' key={userOptions.CHANGE_PASSWORD} className={styles.menuItemOptions}>
          <Icon name='refresh' theme={theme} className={styles.icon} />
          <span>{t('users.resetPassword')}</span>
        </Menu.Item>
      </Menu>
    );
  };

  function onEditUserOption (user: UserMapData) {
    if (company._id === LARVIA_ID) {
      dispatch(usersSlice.fetchSellers());
      dispatch(usersSlice.fetchUnassignedPromoters());
    }

    dispatch(usersSlice.setShowEditUserModal(true));
    dispatch(usersSlice.fetchUser(user._id));
    if (isRunningOnboarding) {
      dispatch(goToOnboardingNextStep(1000));
    }
  }

  function onChangePasswordOption (user: UserMapData) {
    dispatch(usersSlice.setShowResetPasswordModal(true));
    dispatch(usersSlice.fetchUser(user._id));
    if (isRunningOnboarding) {
      dispatch(goToOnboardingNextStep(1000));
    }
  }

  const searchUsers = (props: { dataIndex: string; confirm: (param?: FilterConfirmProps | undefined) => void; active?: boolean | undefined; value: Key[] }) => {
    const { dataIndex, confirm, active, value } = props;
    confirm();

    switch (dataIndex) {
      case 'name':
        dispatch(usersSlice.fetchSearchUsers({ companyId: company._id, active: active, name: value[0] }));
        break;

      case 'email':
        dispatch(usersSlice.fetchSearchUsers({ companyId: company._id, active: active, email: value[0] }));
        break;
    }
  };

  function getColumnSearchProps (dataIndex: string, ref: MutableRefObject<InputRef | null>) {
    const active = getValueActiveUsers(userStatus);
    const label = `users.paramsSearch.${dataIndex}`;
    const placeholder = `${t('users.search')} ${t(label)}`;

    return {
      filterDropdown: (props: FilterDropdownProps) => {
        const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props;
        return (
          <div className={styles.menuSearch}>
            <LrvInput
              theme='light'
              id={`txt_${dataIndex}_search`}
              ref={ref}
              placeholder={placeholder}
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => {
                if (selectedKeys[0]) {
                  clearFilters && clearFilters();
                  searchUsers({ dataIndex, confirm, active, value: selectedKeys });
                }
              }}
              className={styles.inputSearch}
            />
            <Space>
              <Button
                id={`btn_${dataIndex}_search`}
                type='primary'
                onClick={() => {
                  clearFilters && clearFilters();
                  searchUsers({ dataIndex, confirm, active, value: selectedKeys });
                }}
                icon={<SearchOutlined />}
                size='small'
                className={styles.buttonSearch}
                disabled={!selectedKeys[0]}
              >
                {t('users.search')}
              </Button>
              <Button
                id={`btn_${dataIndex}_reset`}
                onClick={() => {
                  clearFilters && clearFilters();
                  dispatch(usersSlice.fetchUsers({ company: company, active: active }));
                }}
                size='small'
                className={styles.buttonReset}
              >
                {t('users.reset')}
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => <SearchOutlined className={isLightTheme ? styles.searchOutlinedIconLight : styles.searchOutlinedIconDark} style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value: boolean | Key, record: UserMapData) => {
        const item: GenericParam = record;
        const field: string = item[dataIndex].toString().toLowerCase();
        return field.includes(`${value}`.toLowerCase());
      },
      onFilterDropdownOpenChange: (visible: boolean) => {
        if (visible) {
          setTimeout(() => ref.current?.select(), 100);
        }
      },
    };
  }

  const onClickAddUser = () => {
    dispatch(usersSlice.setSelectedUser({ _id: '', companyId: '', firstName: '', lastName: '', email: '', active: true, roles: [], allowCompanyBalanceConsumption: false }));
    dispatch(usersSlice.setShowCreateUserModal(true));

    if (company._id === LARVIA_ID) {
      dispatch(usersSlice.fetchSellers());
    }
  };

  return <div className={styles.users}>
    <UserSubHeader onClickAddUser={onClickAddUser} theme={theme} />

    <Row className={styles.rowFlex}>
      <LrvTable
        className={styles.tableUsers}
        rowClassName={(record) => cx(styles.userRow, selectedUser._id === record._id ? styles.selectedRow : '')}
        columns={columns}
        dataSource={usersData}
        loading={isUsersLoading}
        scroll={{ y: '' }}
        theme={theme}
        size='small'
        pagination={{
          size: 'default',
        }}
      />
    </Row>

    <NewUserForm/>
    <EditUserForm />
    <AssigmentBalances theme='light' />
    <ResetPasswordForm />
  </div >;
}

export default Users;
